import { memo, useCallback, useMemo } from 'react';

import {
  BooleanOption,
  Button,
  Flex,
  Heading,
  Label,
  NumericOption,
  OptionGroup,
  SelectOption,
  Separator,
  SliderOption,
  Text,
} from '@/components/core';
import { DEFAULT_MAX_PAGES } from '@/constants';
import { useModelConfigStore } from '@/store';

import { CONFIDENCE_VERSIONS, LLM_MODELS, toNumber, USER_SETTINGS_DEFAULT, UserSettings } from './utils';

const IModelSettings = () => {
  const metadata = useModelConfigStore((state) => state.metadata);
  const preprocessConfig = useModelConfigStore((state) => state.preprocessConfig);
  const updateMetadata = useModelConfigStore((state) => state.updateMetadata);
  const updatePreprocessConfig = useModelConfigStore((state) => state.updatePreprocessConfig);
  const updatePostprocessConfig = useModelConfigStore((state) => state.updatePostprocessConfig);
  const llmVersion = useModelConfigStore((state) => state.llmVersion);
  const setLLmVersion = useModelConfigStore((state) => state.setLlmVersion);
  const confidenceVersion = useModelConfigStore((state) => state.confidenceVersion);
  const setConfidenceVersion = useModelConfigStore((state) => state.setConfidenceVersion);

  const userSettings: UserSettings = useMemo(
    () => ({
      ...USER_SETTINGS_DEFAULT,
      ...(metadata?.['user:settings'] as UserSettings),
    }),
    [metadata]
  );

  const updateSetting = useCallback(
    (key: string, value: string | number) => {
      updateMetadata({ 'user:settings': { ...userSettings, [key]: value } });
    },
    [updateMetadata, userSettings]
  );

  const setNumPages = useCallback(
    (val: number) => {
      const n = toNumber(val, DEFAULT_MAX_PAGES);
      updatePreprocessConfig({ maxPages: n });
      updatePostprocessConfig({ parameters: { n }, strategy: 'BEST_N_PAGES' });
    },
    [updatePostprocessConfig, updatePreprocessConfig]
  );

  const onReset = useCallback(() => {
    const updates: UserSettings = { ...USER_SETTINGS_DEFAULT };
    updateMetadata({ 'user:settings': updates });
    setNumPages(3);
  }, [setNumPages, updateMetadata]);

  return (
    <Flex col className="p-md gap-md">
      <Heading className="uppercase">General</Heading>

      <OptionGroup>
        <SelectOption
          label="Base model"
          items={LLM_MODELS}
          value={llmVersion}
          className="gap-xs items-start"
          onChange={(val) => {
            if (val) setLLmVersion(val.toString());
          }}
          col
        />

        <NumericOption
          label="Page limit"
          description="The maximum number of pages to process in a single document."
          value={preprocessConfig?.maxPages ?? 3}
          onChange={setNumPages}
        />
      </OptionGroup>

      <Separator className="my-2" orientation="horizontal" />

      <Heading className="uppercase">Model training</Heading>

      <OptionGroup>
        <BooleanOption
          label="Continuous AI training"
          description="Use reviewed data to improve accuracy on similar documents."
          isSelected={userSettings?.use_few_shot_learning != null ? userSettings?.use_few_shot_learning : true}
          onChange={(val) => updateSetting('use_few_shot_learning', val)}
          col
        />

        <SliderOption
          label="Learning rate"
          tooltip="A higher value increases the model's likeliness to categorize a prediction as a hallucination."
          value={userSettings.learning_rate}
          step={0.05}
          onChange={(val) => updateSetting('learning_rate', toNumber(val))}
          minValueLabel="Reserved"
          maxValueLabel="Aggresive"
        />

        <SelectOption
          label="Validation engine version"
          tooltip="Select the engine version used to generate confidence estimates. The latest version is recommended."
          items={CONFIDENCE_VERSIONS}
          value={confidenceVersion}
          className="gap-xs items-start"
          onChange={(val) => {
            if (val) setConfidenceVersion(val.toString());
          }}
          col
        />
      </OptionGroup>

      <Separator className="my-2" orientation="horizontal" />

      <Button className="w-fit self-end" variant="secondary" onPress={onReset}>
        <Text>Reset to defaults</Text>
      </Button>
    </Flex>
  );
};

export const ModelSettings = memo(IModelSettings);
